import React, { useEffect, useState } from "react";
import "./CustomField.css";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import toast from "react-hot-toast";
import Loading from "../../components/AWBDashboard/Loading";
import SearchInput from "../../common/SearchInput";
import CustomFieldDrawer from "./CustomFieldDrawer";
import { DROPDOWN_FIELD_TYPE_ID } from "./customField.types";
import {
  fetchFieldTypes,
  fetchCustomForms,
  createCustomForm,
  updateCustomForm,
  deleteCustomForm,
} from "./customField.service";

function ManageCustomField() {
  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);
  const [allField, setAllField] = useState<any[]>([]);
  const [singleField, setSingleField] = useState<any>({
    name: "",
    description: "",
    fieldType: "",
  });
  const [dropdownField, setDropdownField] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<string[]>(["High-1"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // debounce search effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fix the debounced effect — pass the string directly
  useEffect(() => {
    getCustomForms(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    getCustomForms(); // called only on first render
  }, []);

  // ── Loader: field types + forms (same sequence/behavior as before) ──
  const getCustomForms = async (search = "") => {
    try {
      setIsLoading(true);
      const fieldTypes = await fetchFieldTypes();
      setDropdownField(fieldTypes);

      const data = await fetchCustomForms(search);
      setAllField(data.filter((item: any) => item.active === true));
    } catch (error: any) {
      console.error(error);
      setAllField([]);
      setDropdownField([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Validation (unchanged) ──
  const validateFields = (data: any) => {
    const newErrors: any = {};

    if (!data.fieldName?.trim()) {
      newErrors.fieldName = "Field name is required";
    }
    if (!data.fieldType?.trim()) {
      newErrors.fieldType = "Please select a field type";
    }
    if (!data.description?.trim()) {
      newErrors.description = "Description is required";
    }

    const hasSelected = options?.some((row) => row === "");

    if (data.fieldType === DROPDOWN_FIELD_TYPE_ID && options.length === 0) {
      newErrors.options = hasSelected
        ? "Please fill the options"
        : "Please add at least one option";
    }
    if (data.fieldType === DROPDOWN_FIELD_TYPE_ID && hasSelected) {
      newErrors.options = "Please fill the options";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Options handlers (unchanged) ──
  const addOption = () => {
    setOptions([...options, ""]);
  };
  const handleChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };
  const removeOption = () => {
    if (options.length > 1) {
      setOptions(options.slice(0, -1));
    }
  };

  // ── Required/Unique coupling (unchanged) ──
  const handleUniqueChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSingleField((prev: any) => ({
      ...prev,
      isUnique: checked,
      required: prev.required ? prev.required : checked,
    }));
  };
  const handleRequiredChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSingleField((prev: any) => ({
      ...prev,
      required: prev.isUnique ? true : checked,
    }));
  };

  // ── CRUD wrappers (same toasts / side-effects) ──
  const handleCreate = async (payload: any) => {
    try {
      await createCustomForm(payload, options);
      setOpen(false);
      getCustomForms();
      toast.success("Field created successfully!");
    } catch (error: any) {
      toast.error("Oops! Something went wrong. Please try again.");
    }
  };

  const handleUpdate = async (id: any, updates: any) => {
    try {
      await updateCustomForm(id, updates, options);
      getCustomForms();
      setOpen(false);
      toast.success("Field updated successfully!");
    } catch (error: any) {
      toast.error("Oops! Something went wrong. Please try again.");
    }
  };

  const handleDelete = async (id: any) => {
    try {
      await deleteCustomForm(id);
      toast.success("Field deleted successfully!");
      getCustomForms();
    } catch (error: any) {
      toast.error("Oops! Something went wrong. Please try again.");
    }
  };

  // ── Save click: validate → duplicate check → create/update (unchanged) ──
  const handleSave = () => {
    if (!validateFields(singleField)) return;

    const normalizeName = (name: string) => name.trim().toLowerCase();
    const isDuplicate = allField.some((field: typeof singleField) => {
      if (!field.fieldName || !singleField.fieldName) return false;
      return (
        normalizeName(field.fieldName) === normalizeName(singleField.fieldName) &&
        field._id !== singleField._id
      );
    });

    if (isDuplicate) {
      alert("Field name already exists!");
      return;
    }

    if (singleField._id) {
      handleUpdate(singleField._id, singleField);
    } else {
      handleCreate(singleField);
    }
  };

  // ── Confirm dialog ──
  const handleClickOpen = () => setConfirmOpen(true);
  const handleClose = () => setConfirmOpen(false);

  // ── Drawer close variants (preserve original behavior) ──
  const closeDrawer = () => setOpen(false);
  const cancelDrawer = () => {
    setOpen(false);
    setSingleField({});
  };

  return (
    <>
      {/* Delete confirmation */}
      <div className={`ctpl-overlay${confirmOpen ? " open" : ""}`} onClick={handleClose} />
      <div className={`cf-confirm${confirmOpen ? " open" : ""}`} role="dialog" aria-modal="true">
        <div className="cf-confirm-icon">
          <DeleteIcon />
        </div>
        <h2 className="cf-confirm-title">Are you sure?</h2>
        <p className="cf-confirm-text">
          This action will permanently delete
          {singleField?.fieldName ? (
            <> the field <strong>“{singleField.fieldName}”</strong>.</>
          ) : (
            <> this field.</>
          )}
        </p>
        <div className="cf-confirm-actions">
          <button className="ctpl-btn ctpl-btn-ghost" onClick={handleClose}>
            <ArrowBackIcon fontSize="small" /> Back
          </button>
          <button
            className="ctpl-btn cf-btn-danger"
            onClick={() => {
              handleDelete(singleField._id);
              handleClose();
            }}
          >
            <DeleteIcon fontSize="small" /> Delete
          </button>
        </div>
      </div>
      {/* Drawer */}
      <CustomFieldDrawer
        open={open}
        singleField={singleField}
        setSingleField={setSingleField}
        dropdownField={dropdownField}
        options={options}
        setOptions={setOptions}
        addOption={addOption}
        removeOption={removeOption}
        handleChange={handleChange}
        errors={errors}
        onRequiredChange={handleRequiredChanged}
        onUniqueChange={handleUniqueChanged}
        onClose={closeDrawer}
        onCancel={cancelDrawer}
        onSave={handleSave}
      />


      {/* Card */}
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput placeholder="Search fields" onChange={setSearchQuery} width={340} />
          <div className="ctpl-spacer" />
          <button
            className="ctpl-btn ctpl-btn-primary"
            onClick={() => {
              if (allField.length >= 19) {
                toast.error("You can't add more than 20 fields");
              } else {
                setOpen(true);
                setSingleField({});
                setOptions([]);
                setErrors({});
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add New Field
          </button>
        </div>

        <div className="cf-list-scroll">
          {isLoading ? (
            <div className="flex justify-center items-center h-[200px] relative">
              <Loading />
            </div>
          ) : allField.length === 0 ? (
            <div className="cf-empty">No custom fields found</div>
          ) : (
            allField.map((field: any) => (
              <div
                key={field._id}
                className={`fieldListing ${selectedIndex === field._id ? "selected" : ""}`}
                onClick={() => setSelectedIndex(field._id)}
              >
                <div className="fieldHeader">
                  <span className="fieldName">
                    {field.fieldName}{" "}
                    <span className="mandatory-indicator">{field.required ? "*" : ""}</span>
                  </span>
                  <div className="fieldHeaderRight">
                    {field.isUnique && <span className="uniqueBadge">Unique</span>}
                    <span className="fieldType">
                      {dropdownField.find((item: any) => item._id === field.fieldType)?.name || ""}
                    </span>
                  </div>
                </div>
                <div className="fieldsDescription">{field.description}</div>
                <div className="fieldActions">
                  <button
                    className="btn btnYellow"
                    onClick={() => {
                      setOpen(true);
                      setSingleField(field);
                      setOptions(field.dropdownOptions);
                      setErrors({});
                    }}
                  >
                    <EditIcon />
                    Edit
                  </button>
                  <button
                    className="btn btnBlack"
                    onClick={() => {
                      handleClickOpen();
                      setSingleField(field);
                    }}
                  >
                    <DeleteIcon />
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default ManageCustomField;